import { ErrorOutline } from '@mui/icons-material'
import { Box, Stack, Tooltip } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import type { Theme } from '@mui/material'
import type { ReactNode } from 'react'

export const SPREADSHEET_CELL_INSET = 3

type SpreadsheetCellProps = {
  children: ReactNode
  readOnly?: boolean
  disablePadding?: boolean
  error?: string
  /** Put on the element carrying the message, so an input inside can point `aria-describedby` at it. */
  errorId?: string
}

const toBackgroundColor = (theme: Theme, { readOnly, error }: Pick<SpreadsheetCellProps, 'readOnly' | 'error'>) => {
  if (error != null) return theme.palette.bg.danger.muted
  if (readOnly) return theme.palette.bg.surface.subtle
  return undefined
}

/** NOTE: the tooltip wraps the icon, never `children` — wrapping the cell only once an error appears would change the element type there, remounting the subtree and dropping focus mid-edit. */
export const SpreadsheetCell = ({ children, readOnly, disablePadding, error, errorId }: SpreadsheetCellProps) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={(theme) => ({
      width: '100%',
      height: '100%',
      paddingInline: disablePadding ? undefined : theme.spacing(SPREADSHEET_CELL_INSET),
      backgroundColor: toBackgroundColor(theme, { readOnly, error }),
    })}
  >
    {children}
    {error != null && (
      <>
        {/* The icon's own label is not reachable through `aria-describedby`, so the message needs an element of its own for an input in this cell to point at. */}
        {errorId != null && (
          <Box component="span" id={errorId} sx={visuallyHidden}>
            {error}
          </Box>
        )}
        <Tooltip title={error} placement="top">
          <ErrorOutline
            role="img"
            aria-label={error}
            // SvgIcon hides itself from assistive tech unless given `titleAccess`, which would also stack a native tooltip on this one.
            aria-hidden={false}
            fontSize="small"
            sx={(theme) => ({
              flexShrink: 0,
              color: 'text.danger.default',
              // `disablePadding` hands the inset to the input, so the icon supplies its own.
              marginInlineEnd: disablePadding ? theme.spacing(SPREADSHEET_CELL_INSET) : undefined,
            })}
          />
        </Tooltip>
      </>
    )}
  </Stack>
)
